import React, { useReducer, useState } from 'react';
import {
    Typography,
    Button,
    Grid,
    ButtonGroup,
    Paper,
} from '@material-ui/core';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import GetAppIcon from '@material-ui/icons/GetApp';
import LanguageIcon from '@material-ui/icons/Language';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import { makeStyles } from '@material-ui/core/styles';

import Popup from './Popup';
import Select from './Select';
import Input from './Input';

const submitInitialState = {
    running: false,
    file: null,
};

const reduceExportModalSubmit = (state, action) => {
    switch (action.type) {
        case 'init':
            return {
                running: true,
                file: action.file,
            }
        case 'clear':
            return submitInitialState;
        default: return state;
    }
}

const ExportModal = ({ open = false, onClose, handleExport, title = "Exportar dados" }) => {
    const classes = useStyles();

    const [submit, dispatchSubmit] = useReducer(reduceExportModalSubmit, submitInitialState);
    const [exportDestination, setExportDestination] = useState("website");

    const [email, setEmail] = useState("");
    const [confirmEmail, setConfirmEmail] = useState("");

    const onClickCSV = async () => {
        dispatchSubmit({
            type: 'init',
            file: 'csv',
        });

        await handleExport('csv', exportDestination, email);

        dispatchSubmit({
            type: 'clear',
        });
        onClose();
    };
    const onClickXLSX = async () => {
        dispatchSubmit({
            type: 'init',
            file: 'xlsx',
        });

        await handleExport('xlsx', exportDestination, email);

        dispatchSubmit({
            type: 'clear',
        });
        onClose();
    }
    const onClickPDF = async () => {
        dispatchSubmit({
            type: 'init',
            file: 'pdf',
        });

        await handleExport('pdf', exportDestination, email);

        dispatchSubmit({
            type: 'clear',
        });
        onClose();
    }

    const handleExportDestination = ({ target }) => {
        setExportDestination(target.value);
        setEmail("");
        setConfirmEmail("");
    }

    return (
        <Popup
            title={title}
            openPopup={open}
            onClose={onClose}
            width="92%"
            maxWidth="sm"
        >
            <div className={classes.exportModal}>
                <div className={classes.exportIntro}>
                    <div className={classes.exportIntroIcon}>
                        <GetAppIcon />
                    </div>
                    <div>
                        <Typography className={classes.exportIntroTitle}>
                            Prepare a exportação
                        </Typography>
                        <Typography className={classes.exportIntroText}>
                            Escolha onde receber o arquivo e o formato.
                        </Typography>
                    </div>
                </div>

                <Paper elevation={0} className={classes.exportSection}>
                    <div className={classes.exportSectionHeader}>
                        <div className={classes.exportSectionIcon}>
                            {exportDestination === 'email' ? <EmailOutlinedIcon /> : <LanguageIcon />}
                        </div>
                        <div>
                            <Typography className={classes.exportSectionTitle}>
                                Destino do arquivo
                            </Typography>
                            <Typography className={classes.exportSectionDescription}>
                                Baixe nesta página ou envie o arquivo por e-mail.
                            </Typography>
                        </div>
                    </div>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography className={classes.fieldLabel}>Receber exportação</Typography>
                            <Select
                                options={[
                                    { label: 'Via aplicação (página atual)', value: 'website' },
                                    { label: 'Via e-mail', value: 'email' },
                                ]}
                                textFieldProps={{
                                    onChange: handleExportDestination,
                                    value: exportDestination,
                                    fullWidth: true,
                                }}
                            />
                        </Grid>
                        {
                            exportDestination === "email" && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <Typography className={classes.fieldLabel}>E-mail</Typography>
                                        <Input
                                            textFieldProps={{
                                                placeholder: 'E-mail que receberá o arquivo',
                                                value: email,
                                                onChange: ({ target }) => setEmail(target.value),
                                                fullWidth: true,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography className={classes.fieldLabel}>Confirmar e-mail</Typography>
                                        <Input
                                            textFieldProps={{
                                                placeholder: 'Digite o e-mail novamente',
                                                value: confirmEmail,
                                                onChange: ({ target }) => setConfirmEmail(target.value),
                                                fullWidth: true,
                                            }}
                                        />
                                    </Grid>
                                    {
                                        exportDestination === "email" && (
                                            <Grid item xs={12}>
                                                {
                                                    email === "" && confirmEmail === "" ? (
                                                        <div className={classes.emailHint}>
                                                            Insira e confirme o e-mail que receberá o arquivo.
                                                        </div>
                                                    ) : email !== confirmEmail ? (
                                                        <div className={classes.emailError}>
                                                            Os e-mails informados não coincidem.
                                                        </div>
                                                    ) : (
                                                        <div className={classes.emailSuccess}>
                                                            E-mail confirmado.
                                                        </div>
                                                    )
                                                }
                                            </Grid>
                                        )
                                    }
                                </>
                            )
                        }
                    </Grid>
                </Paper>

                <Paper elevation={0} className={classes.exportSection}>
                    <div className={classes.exportSectionHeader}>
                        <div className={classes.exportSectionIcon}>
                            <InsertDriveFileOutlinedIcon />
                        </div>
                        <div>
                            <Typography className={classes.exportSectionTitle}>
                                Formato do arquivo
                            </Typography>
                            <Typography className={classes.exportSectionDescription}>
                                Selecione uma opção para iniciar a exportação.
                            </Typography>
                        </div>
                    </div>

                    <ButtonGroup
                        variant="contained"
                        color="primary"
                        fullWidth={true}
                        className={classes.formatButtonGroup}
                        disabled={
                            (
                                (exportDestination === "email" && email === "" && confirmEmail === "") ||
                                (exportDestination === "email" && email !== confirmEmail)
                            ) ? true : false
                        }
                    >
                        <Button
                            variant='contained'
                            color='primary'
                            fullWidth={true}
                            onClick={onClickCSV}
                            disabled={submit.running && submit.file === 'csv'}
                        >
                            <span className={classes.formatButtonLabel}>
                                <strong>CSV</strong>
                                
                            </span>
                        </Button>
                        <Button
                            variant='contained'
                            color='primary'
                            fullWidth={true}
                            onClick={onClickXLSX}
                            disabled={submit.running && submit.file === 'xlsx'}
                        >
                            <span className={classes.formatButtonLabel}>
                                <strong>XLSX</strong>
                               
                            </span>
                        </Button>
                        <Button
                            variant='contained'
                            color='primary'
                            fullWidth={true}
                            onClick={onClickPDF}
                            disabled={submit.running && submit.file === 'pdf'}
                        >
                            <span className={classes.formatButtonLabel}>
                                <strong>PDF</strong>
                            
                            </span>
                        </Button>
                    </ButtonGroup>
                </Paper>

                <div className={classes.exportFooter}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        className={classes.cancelButton}
                    >
                        Cancelar
                    </Button>
                </div>
            </div>
            <Backdrop
                open={submit.running}
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            >
                <Typography
                    variant='h6'
                    style={{
                        textAlign: 'center',
                        userSelect: 'none',
                    }}
                >
                    <CircularProgress color='inherit' />
                    <br />
                    GERANDO O ARQUIVO...
                </Typography>
            </Backdrop>
        </Popup>
    );
}

const useStyles = makeStyles((theme) => ({
    exportModal: {
        color: '#1f2937',
        paddingTop: theme.spacing(.5),
    },
    exportIntro: {
        alignItems: 'center',
        background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)',
        border: '1px solid #dbeafe',
        borderRadius: 14,
        display: 'flex',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            alignItems: 'flex-start',
            padding: theme.spacing(1.5),
        },
    },
    exportIntroIcon: {
        alignItems: 'center',
        backgroundColor: '#1976d2',
        borderRadius: 11,
        color: '#fff',
        display: 'flex',
        flexShrink: 0,
        height: 42,
        justifyContent: 'center',
        width: 42,
        '& svg': {
            fontSize: 22,
        },
    },
    exportIntroTitle: {
        color: '#172033',
        fontSize: '1rem',
        fontWeight: 700,
    },
    exportIntroText: {
        color: '#64748b',
        fontSize: '.85rem',
        lineHeight: 1.5,
        marginTop: 2,
    },
    exportSection: {
        backgroundColor: '#fbfdff',
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            borderLeft: 0,
            borderRadius: 0,
            borderRight: 0,
            marginLeft: theme.spacing(-2),
            marginRight: theme.spacing(-2),
        },
    },
    exportSectionHeader: {
        alignItems: 'center',
        display: 'flex',
        gap: theme.spacing(1.25),
        marginBottom: theme.spacing(2),
    },
    exportSectionIcon: {
        alignItems: 'center',
        backgroundColor: '#e3f2fd',
        borderRadius: 10,
        color: '#1976d2',
        display: 'flex',
        flexShrink: 0,
        height: 38,
        justifyContent: 'center',
        width: 38,
        '& svg': {
            fontSize: 20,
        },
    },
    exportSectionTitle: {
        color: '#1e293b',
        fontSize: '.95rem',
        fontWeight: 700,
    },
    exportSectionDescription: {
        color: '#64748b',
        fontSize: '.78rem',
        lineHeight: 1.4,
        marginTop: 1,
    },
    fieldLabel: {
        color: '#64748b',
        fontSize: '.72rem',
        fontWeight: 700,
        letterSpacing: '.02em',
        marginBottom: theme.spacing(.6),
    },
    emailHint: {
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 9,
        color: '#64748b',
        fontSize: '.8rem',
        padding: theme.spacing(1, 1.25),
    },
    emailError: {
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 9,
        color: '#b91c1c',
        fontSize: '.8rem',
        fontWeight: 600,
        padding: theme.spacing(1, 1.25),
    },
    emailSuccess: {
        backgroundColor: '#ecfdf5',
        border: '1px solid #a7f3d0',
        borderRadius: 9,
        color: '#047857',
        fontSize: '.8rem',
        fontWeight: 600,
        padding: theme.spacing(1, 1.25),
    },
    formatButtonGroup: {
        display: 'grid',
        gap: theme.spacing(1),
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        '& .MuiButtonGroup-grouped': {
            border: '0 !important',
            borderRadius: '9px !important',
            margin: '0 !important',
            minWidth: 0,
            width: 'auto',
        },
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
        },
    },
    formatButtonLabel: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: theme.spacing(.5, 0),
        textTransform: 'none',
        '& strong': {
            fontSize: '.95rem',
            letterSpacing: '.03em',
        },
        '& small': {
            fontSize: '.68rem',
            fontWeight: 400,
            lineHeight: 1.25,
            opacity: .85,
        },
    },
    exportFooter: {
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'flex-end',
        margin: theme.spacing(0, -3, -2.5),
        padding: theme.spacing(2, 3),
        [theme.breakpoints.down('xs')]: {
            margin: theme.spacing(0, -2, -2.5),
            padding: theme.spacing(1.5, 2),
        },
    },
    cancelButton: {
        borderColor: '#cbd5e1',
        borderRadius: 9,
        color: '#475569',
        fontWeight: 600,
        minWidth: 140,
        textTransform: 'none',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
}));

export default ExportModal;
